import {
  useV2ListSpecialConditionsForClientQuery,
  useV2CreateSpecialConditionForClientMutation,
  useV2UpdateClientSpecialConditionMutation,
} from "@generated";
import type { V2SpecialConditionResource } from "@generated";

export type SpecialConditionStatus = "active" | "skipped" | "optional";

export interface SpecialConditionOverride {
  id: string;
  serviceConditionId: string;
  content: string;
  status: SpecialConditionStatus;
}

// A client's special-condition overrides, plus create/update helpers.
export const useClientSpecialConditions = (clientId: string) => {
  const { data, isLoading, refetch } = useV2ListSpecialConditionsForClientQuery(
    {
      clientId: clientId,
      // Include the service-condition so the relationship linkage is present and
      // each override can be matched to the condition it overrides.
      include: ["service-condition"],
      page: { size: 100 },
    },
    { refetchOnMountOrArgChange: true }
  );

  const overrides: SpecialConditionOverride[] = (data?.data ?? []).map(
    (sc) => ({
      id: String(sc.id),
      serviceConditionId: String(
        sc.relationships?.["service-condition"]?.data?.id ?? ""
      ),
      content: sc.attributes?.content ?? "",
      status: (sc.attributes?.status ?? "active") as SpecialConditionStatus,
    })
  );

  const [create] = useV2CreateSpecialConditionForClientMutation();
  const [update] = useV2UpdateClientSpecialConditionMutation();

  const createOverride = (
    serviceConditionId: string,
    content: string,
    status: SpecialConditionStatus
  ) =>
    create({
      clientId: clientId,
      body: {
        data: {
          type: "special-conditions",
          attributes: { content, status },
          relationships: {
            "service-condition": {
              data: {
                type: "service-conditions",
                id: serviceConditionId,
              },
            },
          },
        } as unknown as V2SpecialConditionResource,
      },
    }).unwrap();

  const updateOverride = (
    id: string,
    content: string,
    status: SpecialConditionStatus
  ) =>
    update({
      clientId: clientId,
      id: id,
      body: {
        data: {
          type: "special-conditions",
          id: id,
          attributes: { content, status },
        } as unknown as V2SpecialConditionResource,
      },
    }).unwrap();

  return { overrides, isLoading, refetch, createOverride, updateOverride };
};

export default useClientSpecialConditions;
